import { type StateCreator, create } from "zustand";
import { devtools } from "zustand/middleware";
import { setAttachModeActive } from "./attach-mode-active";
import { buildLockedFilterValues, cloneFilterValues, getAttachTargetKey, isSameAttachTarget } from "./attach-mode";
import { useStore as useEditStore } from "./edit-store";
import { useStore as useGameStore } from "./store";
import { type AttachModeTarget, type EventFilterValue, type MergedGpEventResponse, VideoSidebar } from "./types";

export enum AttachModeSidebarView {
	EventList = "eventList",
	Filters = "filters",
}

const initialAttachState = {
	target: null as AttachModeTarget | null,
	savedFilterValues: [] as EventFilterValue[],
	savedFilteredFilterValues: [] as EventFilterValue[],
	sidebarView: AttachModeSidebarView.EventList,
	attachedEventsByTargetKey: {} as Record<string, MergedGpEventResponse[]>,
};

interface AttachState {
	target: AttachModeTarget | null;
	savedFilterValues: EventFilterValue[];
	savedFilteredFilterValues: EventFilterValue[];
	sidebarView: AttachModeSidebarView;
	attachedEventsByTargetKey: Record<string, MergedGpEventResponse[]>;
	enterAttachMode: (target: AttachModeTarget) => void;
	exitAttachMode: () => void;
	reset: () => void;
	setSidebarView: (sidebarView: AttachModeSidebarView) => void;
	refreshAttachLock: () => void;
	setAttachedEvents: (target: AttachModeTarget, events: MergedGpEventResponse[]) => void;
}

type Store = StateCreator<AttachState>;

function applyAttachFilters(filterValues: EventFilterValue[]) {
	const gameStore = useGameStore.getState();
	const nextFilterValues = cloneFilterValues(filterValues);
	gameStore.setFilterValues(nextFilterValues);
	gameStore.setFilteredFilterValues(cloneFilterValues(nextFilterValues));
}

function openAttachModeSidebar() {
	useGameStore.getState().setSidebar(VideoSidebar.AttachMode);
}

function clearAttachSidebarIfOpen() {
	const gameStore = useGameStore.getState();
	if (gameStore.sidebar === VideoSidebar.AttachMode) {
		gameStore.setSidebar(VideoSidebar.TeamPlayerReviews);
	}
}

const store: Store = (set, get) => ({
	...initialAttachState,
	enterAttachMode: (target) => {
		const gameStore = useGameStore.getState();
		const eventFilter = gameStore.eventFilter;
		const currentTarget = get().target;

		if (isSameAttachTarget(currentTarget, target)) {
			set({ sidebarView: AttachModeSidebarView.EventList });
			get().refreshAttachLock();
			openAttachModeSidebar();
			return;
		}

		const lockedFilterValues = buildLockedFilterValues(eventFilter, target);

		if (currentTarget === null) {
			setAttachModeActive(true);
			set({
				target,
				savedFilterValues: cloneFilterValues(gameStore.filterValues),
				savedFilteredFilterValues: cloneFilterValues(gameStore.filteredFilterValues),
				sidebarView: AttachModeSidebarView.EventList,
			});
		} else {
			const selectedEvents = useEditStore.getState().selectedEvents;
			set((state) => ({
				target,
				sidebarView: AttachModeSidebarView.EventList,
				attachedEventsByTargetKey: {
					...state.attachedEventsByTargetKey,
					[getAttachTargetKey(currentTarget)]: [...selectedEvents],
				},
			}));
		}

		applyAttachFilters(lockedFilterValues);
		const attachedEvents = get().attachedEventsByTargetKey[getAttachTargetKey(target)] ?? [];
		useEditStore.getState().setSelectedEvents(attachedEvents);
		openAttachModeSidebar();
	},
	exitAttachMode: () => {
		const { savedFilterValues, savedFilteredFilterValues, target } = get();
		if (target === null) {
			return;
		}

		const gameStore = useGameStore.getState();
		gameStore.setFilterValues(cloneFilterValues(savedFilterValues));
		gameStore.setFilteredFilterValues(cloneFilterValues(savedFilteredFilterValues));
		useEditStore.getState().setSelectedEvents([]);
		clearAttachSidebarIfOpen();

		setAttachModeActive(false);
		set({
			target: null,
			savedFilterValues: [],
			savedFilteredFilterValues: [],
			sidebarView: AttachModeSidebarView.EventList,
		});
	},
	reset: () => {
		const { savedFilterValues, savedFilteredFilterValues, target } = get();

		if (target !== null) {
			const gameStore = useGameStore.getState();
			gameStore.setFilterValues(cloneFilterValues(savedFilterValues));
			gameStore.setFilteredFilterValues(cloneFilterValues(savedFilteredFilterValues));
		}

		useEditStore.getState().setSelectedEvents([]);
		clearAttachSidebarIfOpen();
		setAttachModeActive(false);
		set({ ...initialAttachState });
	},
	setSidebarView: (sidebarView) => set({ sidebarView }),
	refreshAttachLock: () => {
		const { target } = get();
		if (target === null) {
			return;
		}

		const { eventFilter } = useGameStore.getState();
		applyAttachFilters(buildLockedFilterValues(eventFilter, target));
	},
	setAttachedEvents: (target, events) => {
		set((state) => ({
			attachedEventsByTargetKey: {
				...state.attachedEventsByTargetKey,
				[getAttachTargetKey(target)]: events,
			},
		}));
	},
});

export const useStore = create<AttachState>()(
	devtools(store, {
		//@ts-ignore
		enabled: import.meta.env.DEV,
		name: "game-attach-store",
	})
);

export const useAttachStore = () => useStore((state) => state);
